import { useCallback, useEffect, useState, type RefObject } from "react";

/**
 * The measured inline size of an element, for layout decisions CSS cannot make.
 *
 * A container query answers "how wide am I" in a stylesheet, but a table that has to decide *which
 * columns to render* is deciding in JavaScript, and neither the viewport nor any one page's pane is
 * the right thing to ask: a fee-schedule table sits inside a templates column inside a proposal pane
 * inside the window, and every one of those takes a share before the table sees what is left.
 *
 * Undefined means nothing has been measured — first render, or a renderer without `ResizeObserver`
 * such as jsdom — and callers should fall back to whatever they would have done before measuring.
 */
export default function useMeasuredWidth(): [(node: HTMLElement | null) => void, number | undefined] {
  const [width, setWidth] = useState<number | undefined>(undefined);

  const ref = useCallback((node: HTMLElement | null) => {
    // React 19 retires a ref by calling the cleanup this returns rather than by calling the ref with
    // null, so the detach path is the returned function. The guard is for the null call React makes
    // when the previous render returned no cleanup — which is what happens without ResizeObserver.
    if (!node) return;
    if (typeof ResizeObserver === "undefined") return;

    // The observer reports once on `observe`, so the mount-time width arrives without measuring here
    // as well — and it arrives as the content box, which is the box the content has to fit inside.
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setWidth(entry.contentBoxSize?.[0]?.inlineSize ?? entry.contentRect.width);
    });
    observer.observe(node);

    return () => {
      observer.disconnect();
      setWidth(undefined);
    };
  }, []);

  return [ref, width];
}

/**
 * The same measurement, for an element that can only be reached through a ref *object*.
 *
 * Material React Table is the reason this exists. It accepts a ref for its table container through
 * `muiTableContainerProps` and wires it up by assigning `ref.current = node` — it never calls the
 * ref. A callback ref handed to it is therefore never invoked: it quietly acquires a `current`
 * property and reports no width at all, which in a table that sizes its columns from the measurement
 * reads as the column fitting being switched off rather than as a broken ref.
 *
 * Prefer {@link useMeasuredWidth} wherever the element takes a ref of its own. This is for the boxes
 * somebody else owns.
 *
 * The node is read once, after mount: a ref object is not something React can make a dependency of,
 * so there is no render at which its arrival can be observed. Mount is enough for the case this
 * serves, because the owner assigns the node while committing its own subtree — which finishes before
 * this effect runs — and keeps that same node for as long as the table is mounted. An owner that
 * swapped the node out underneath us would go unnoticed, which is the one thing a callback ref would
 * have caught.
 *
 * Undefined means nothing has been measured, exactly as above.
 */
export function useMeasuredWidthOfRef(ref: RefObject<HTMLElement | null>): number | undefined {
  const [width, setWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setWidth(entry.contentBoxSize?.[0]?.inlineSize ?? entry.contentRect.width);
    });
    observer.observe(node);

    return () => {
      observer.disconnect();
      setWidth(undefined);
    };
  }, [ref]);

  return width;
}
